import React, { useState, useEffect } from "react";
import { 
  Grid, 
  TextField, 
  Alert, 
  CircularProgress, 
  Typography, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  Box,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  InputAdornment,
  TablePagination,
  Tooltip
} from "@mui/material";
import { Edit as EditIcon, Visibility as ViewIcon, Delete as DeleteIcon, Search as SearchIcon, Download as DownloadIcon } from "@mui/icons-material";
import * as XLSX from 'xlsx';
import FormContainer from "../../../../components/Forms/FormContainer";
import { 
  UpdateAndGetResponse, 
  DeleteAndGetResponse, 
  ExecutePostWithParams, 
  GetDataFromApiWithParamsAndGetResponse 
} from "../../../../services/api/ExecuteApiRequests";
import { IPDCAttachFile1 } from "../../../../components/Controls/IPDCAttachFile";
import useDecodedUser from "../../../../services/hooks/useDecodedUser";

// prettier-ignore
const initialData = { 
  date_of_visit: "",
  investor_company_name: "",
  country_of_origin: "",
  sector_of_interest: "",
  representative_names: "",
  park_interested_in: "",
  purpose_of_visit: "",
  how_you_know_about_us: "",
  how_you_know_about_us_other: "",
  email: "",
  phone_no: "",
  responsible_ipdc_officer: ""
};

// ==================== VALIDATION FUNCTIONS ====================
const validateEmail = (email) => {
  if (!email) return false; // Required field
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phone) => {
  if (!phone) return false; // Required field
  // Allows: +251912345678, 0912345678, 251912345678, 0112345678
  const phoneRegex = /^(\+?251|0)?[1-9]\d{8}$/;
  return phoneRegex.test(phone);
};

const validateRequired = (value) => {
  return value && value.toString().trim() !== "";
};

// Get error message for fields
const getFieldError = (name, value) => {
  switch (name) {
    case "investor_company_name":
      if (!validateRequired(value)) return "Investor/Company name is required";
      return "";
    case "country_of_origin":
      if (!validateRequired(value)) return "Country of origin is required";
      return "";
    case "representative_names":
      if (!validateRequired(value)) return "Representative name(s) is required";
      return "";
    case "purpose_of_visit":
      if (!validateRequired(value)) return "Purpose of visit is required";
      return "";
    case "how_you_know_about_us":
      if (!validateRequired(value)) return "Please select how you know about us";
      return "";
    case "email":
      if (!validateRequired(value)) return "Email address is required";
      if (!validateEmail(value)) return "Invalid email format. Example: name@domain.com";
      return "";
    case "phone_no":
      if (!validateRequired(value)) return "Phone number is required";
      if (!validatePhoneNumber(value)) return "Invalid phone number. Examples: 0912345678, 0112345678, +251912345678";
      return "";
    case "how_you_know_about_us_other":
      if (value && !validateRequired(value)) return "Please specify how you know about us";
      return "";
    default:
      return "";
  }
};

const howYouKnowOptions = [
  "Web Site",
  "Social Media",
  "News Article",
  "Industry Event",
  "Investor Referral",
  "Other"
];

const sectorOfInterestOptions = [
  "Apparel and Textile",
  "Leather and Leather Products",
  "Pharmaceuticals",
  "Agro-processing",
  "Horticulture",
  "Food and Beverage Processing",
  "Construction Materials",
  "Chemical Products",
  "Automotive and Machinery"
];

// List of all countries
const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
  "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador",
  "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
  "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
  "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
  "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
  "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

export default function RegisterPotentialInvestor() {
  const decodedUser = useDecodedUser();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(initialData);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [parkInterestedOptions, setParkInterestedOptions] = useState([]);
  const [loadingParks, setLoadingParks] = useState(false);
  const [investorsData, setInvestorsData] = useState([]);
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editingId, setEditingId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Country of Origin states
  const [isOtherCountry, setIsOtherCountry] = useState(false);
  const [otherCountryValue, setOtherCountryValue] = useState("");

  useEffect(() => {
    if (decodedUser?.full_name) {
      setFormData(prevData => ({ 
        ...prevData, 
        responsible_ipdc_officer: decodedUser.full_name 
      }));
    }
  }, [decodedUser]);

  useEffect(() => {
    const fetchParks = async () => {
      try {
        setLoadingParks(true);
        const response = await GetDataFromApiWithParamsAndGetResponse("parks");
        if (response && response.data) {
          const parkNames = response.data.map(park => park.name || park.park_name);
          setParkInterestedOptions(parkNames);
        }
      } catch (error) {
        console.error("Failed to fetch parks:", error);
        setParkInterestedOptions([
          "Bole Lemi Special Economic Zone - 1",
          "Bole Lemi Special Economic Zone - 2",
        ]);
      } finally {
        setLoadingParks(false);
      }
    };

    fetchParks();
  }, []);

  useEffect(() => {
    const fetchInvestors = async () => {
      try {
        setLoadingInvestors(true);
        const response = await GetDataFromApiWithParamsAndGetResponse("investors/getpotentialinvestors");
        
        if (response && response.data) {
          setInvestorsData(response.data);
          setFilteredInvestors(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch investors:", error);
        setSnackbar({
          open: true,
          message: "Failed to load investors data",
          severity: "error"
        });
      } finally {
        setLoadingInvestors(false);
      }
    };

    fetchInvestors();
  }, [refreshKey]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredInvestors(investorsData);
    } else {
      const filtered = investorsData.filter(investor => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (investor.investor_company_name && investor.investor_company_name.toLowerCase().includes(searchLower)) ||
          (investor.representative_names && investor.representative_names.toLowerCase().includes(searchLower)) ||
          (investor.country_of_origin && investor.country_of_origin.toLowerCase().includes(searchLower)) ||
          (investor.email && investor.email.toLowerCase().includes(searchLower)) ||
          (investor.phone_no && investor.phone_no.includes(searchTerm)) ||
          (investor.park_interested_in && investor.park_interested_in.toLowerCase().includes(searchLower)) ||
          (investor.sector_of_interest && investor.sector_of_interest.toLowerCase().includes(searchLower))
        );
      });
      setFilteredInvestors(filtered);
    }
    setPage(0);
  }, [searchTerm, investorsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedInvestors = filteredInvestors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const exportToExcel = () => {
    try {
      const exportData = filteredInvestors.map((investor, index) => ({
        'No.': index + 1,
        'Date of Visit': formatDate(investor.date_of_visit),
        'Company Name': investor.investor_company_name,
        'Country of Origin': investor.country_of_origin,
        'Sector of Interest': investor.sector_of_interest,
        'Representative Names': investor.representative_names,
        'Park Interested In': investor.park_interested_in,
        'Purpose of Visit': investor.purpose_of_visit,
        'How You Know About Us': investor.how_you_know_about_us,
        'Email': investor.email,
        'Phone Number': investor.phone_no,
        'Responsible IPDC Officer': investor.responsible_ipdc_officer
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Investors");
      const fileName = `Potential_Investors_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      setSnackbar({
        open: true,
        message: `Exported ${exportData.length} investors to Excel`,
        severity: "success"
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setSnackbar({
        open: true,
        message: "Failed to export data to Excel",
        severity: "error"
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      setRefreshKey(prev => prev + 1);
      setSearchTerm("");
      setPage(0);
    }
  };

  const handleChange = async event => {
    const { name, value } = event.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = getFieldError(name, value);
    if (error) {
      setFormErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateAllFields = () => {
    const errors = {};
    let isValid = true;
    
    // Validate all required fields
    const fieldsToValidate = [
      "investor_company_name", "country_of_origin", "representative_names", 
      "purpose_of_visit", "how_you_know_about_us", "email", "phone_no"
    ];
    
    fieldsToValidate.forEach(field => {
      const value = formData[field];
      const error = getFieldError(field, value);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });
    
    // Validate "Other" field if applicable
    if (formData.how_you_know_about_us === "Other" && !formData.how_you_know_about_us_other) {
      errors.how_you_know_about_us_other = "Please specify how you know about us";
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  // Country of Origin handlers
  const handleCountryChange = async event => {
    const value = event.target.value;
    if (value === "other") {
      setIsOtherCountry(true);
      setFormData(prevData => ({ ...prevData, country_of_origin: "" }));
      setFormErrors(prev => ({ ...prev, country_of_origin: "" }));
    } else {
      setIsOtherCountry(false);
      setFormData(prevData => ({ ...prevData, country_of_origin: value }));
      if (value) {
        setFormErrors(prev => ({ ...prev, country_of_origin: "" }));
      }
    }
    setTouched(prev => ({ ...prev, country_of_origin: true }));
  };

  const handleOtherCountryChange = async event => {
    const value = event.target.value;
    setOtherCountryValue(value);
    setFormData(prevData => ({ ...prevData, country_of_origin: value }));
    if (value) {
      setFormErrors(prev => ({ ...prev, country_of_origin: "" }));
    }
  };

  const handleOtherCountryBlur = () => {
    if (!otherCountryValue) {
      setFormErrors(prev => ({ ...prev, country_of_origin: "Country name is required" }));
    }
  };
  
  const handleSectorChange = (event) => {
    const { value } = event.target;
    const sectorString = Array.isArray(value) ? value.join(", ") : value;
    setFormData(prevData => ({ ...prevData, sector_of_interest: sectorString }));
  };
  
  const handleParkChange = (event) => {
    const { value } = event.target;
    const parkString = Array.isArray(value) ? value.join(", ") : value;
    setFormData(prevData => ({ ...prevData, park_interested_in: parkString }));
  };
  
  const handleAttachmentsChange = files => {
    setAttachedFiles(files);
  };

  const handleSubmit = async event => {
    console.log("Submit started");
    event.preventDefault();
    
    // Mark all fields as touched to show errors
    const allFields = ["investor_company_name", "country_of_origin", "representative_names", "purpose_of_visit", "how_you_know_about_us", "email", "phone_no"];
    const newTouched = {};
    allFields.forEach(field => { newTouched[field] = true; });
    setTouched(newTouched);
    
    // Validate all fields before submission
    if (!validateAllFields()) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors before submitting.",
        severity: "error"
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsLoading(true);
    const formDataWithFiles = new FormData();
    try {
      const dataToSend = { ...formData };
      
      if (dataToSend.how_you_know_about_us === "Other" && dataToSend.how_you_know_about_us_other) {
        dataToSend.how_you_know_about_us = `Other: ${dataToSend.how_you_know_about_us_other}`;
      }
      
      delete dataToSend.how_you_know_about_us_other;
      
      Object.entries(dataToSend).forEach(([key, value]) => {
        formDataWithFiles.append(key, value);
      });

      if (attachedFiles && attachedFiles.length > 0) {
        attachedFiles.forEach((file, index) => {
          formDataWithFiles.append("files", file);
        });
        console.log("Files attached:", attachedFiles.length);
      }

      let responseData;
      if (editingId) {
        responseData = await UpdateAndGetResponse(`investors/potentialinvestors/${editingId}`, formDataWithFiles);
      } else {
        responseData = await ExecutePostWithParams("investors/potentialinvestors", formDataWithFiles, true);
      }
      
      console.log(responseData);
      if (responseData) {
        setSnackbar({
          open: true,
          message: editingId ? "Investor updated successfully!" : "Investor registered successfully!",
          severity: "success"
        });
        setFormData(initialData);
        setFormErrors({});
        setTouched({});
        setAttachedFiles([]);
        setEditingId(null);
        setIsOtherCountry(false);
        setOtherCountryValue("");
        setRefreshKey(prev => prev + 1);
        setActiveTab(1);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to save data",
          severity: "error"
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "An error occurred during registration. Please try again.",
        severity: "error"
      });
      console.log("Failed to save data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (investor) => {
    setEditingId(investor.id);
    
    let howYouKnow = investor.how_you_know_about_us;
    let howYouKnowOther = "";
    
    if (howYouKnow && howYouKnow.startsWith("Other: ")) {
      howYouKnow = "Other";
      howYouKnowOther = investor.how_you_know_about_us.substring(7);
    }
    
    setFormData({
      date_of_visit: investor.date_of_visit || "",
      investor_company_name: investor.investor_company_name || "",
      country_of_origin: investor.country_of_origin || "",
      sector_of_interest: investor.sector_of_interest || "",
      representative_names: investor.representative_names || "",
      park_interested_in: investor.park_interested_in || "",
      purpose_of_visit: investor.purpose_of_visit || "",
      how_you_know_about_us: howYouKnow || "",
      how_you_know_about_us_other: howYouKnowOther,
      email: investor.email || "",
      phone_no: investor.phone_no || "",
      responsible_ipdc_officer: investor.responsible_ipdc_officer || decodedUser?.full_name || ""
    });
    setFormErrors({});
    setTouched({});
    
    setActiveTab(0);
    setEditDialogOpen(false);
  };

  const handleView = (investor) => {
    setSelectedInvestor(investor);
    setViewDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this investor?")) {
      try {
        const response = await DeleteAndGetResponse(`investors/potentialinvestors/${id}`);
        if (response) {
          setSnackbar({
            open: true,
            message: "Investor deleted successfully!",
            severity: "success"
          });
          setRefreshKey(prev => prev + 1);
          setSearchTerm("");
          setPage(0);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to delete investor",
          severity: "error"
        });
        console.error("Error deleting investor:", error);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resetForm = () => {
    setFormData(initialData);
    setFormErrors({});
    setTouched({});
    if (decodedUser?.full_name) {
      setFormData(prev => ({ ...prev, responsible_ipdc_officer: decodedUser.full_name }));
    }
    setAttachedFiles([]);
    setEditingId(null);
    setIsOtherCountry(false);
    setOtherCountryValue("");
  };

  const showError = (fieldName) => {
    return touched[fieldName] && formErrors[fieldName];
  };

  const getFieldLabel = (key) => {
    const labels = {
      date_of_visit: "Date of Visit",
      investor_company_name: "Investor/Company Name",
      country_of_origin: "Country of Origin",
      sector_of_interest: "Sector of Interest",
      representative_names: "Representative Name(s)",
      park_interested_in: "Park Interested in",
      purpose_of_visit: "Purpose of Visit",
      how_you_know_about_us: "How you know about us",
      how_you_know_about_us_other: "Please specify",
      email: "Email",
      phone_no: "Phone No",
      responsible_ipdc_officer: "Responsible IPDC Officer"
    };
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getSelectedSectors = () => {
    if (!formData.sector_of_interest) return [];
    return formData.sector_of_interest.split(", ").filter(item => item.trim() !== "");
  };

  const getSelectedParks = () => {
    if (!formData.park_interested_in) return [];
    return formData.park_interested_in.split(", ").filter(item => item.trim() !== "");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          centered
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 4,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
            },
            '& .MuiTab-root': {
              border: 'none',
              boxShadow: 'none',
              minWidth: 'auto',
              px: 4,
            }
          }}
        >
          <Tab label={editingId ? "Edit Investor" : "Register Investor"} />
          <Tab label="Edit Investors" />
        </Tabs>
        
        {activeTab === 0 && (
          <FormContainer 
            title={editingId ? "Edit Potential Investor" : "Register Visitor/Investor Information"} 
            onSubmit={handleSubmit}
            onReset={editingId ? resetForm : null}
            showResetButton={editingId}
            resetButtonText="Cancel Edit"
          >
            {isLoading && (
              <Grid item xs={12} md={12} alignContent={"center"}>
                <CircularProgress />
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                id="date_of_visit"
                name="date_of_visit"
                label={getFieldLabel("date_of_visit")}
                variant="outlined"
                fullWidth
                required
                value={formData.date_of_visit}
                type="date"
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="investor_company_name"
                name="investor_company_name"
                label={getFieldLabel("investor_company_name")}
                variant="outlined"
                fullWidth
                required
                value={formData.investor_company_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("investor_company_name")}
                helperText={showError("investor_company_name") ? formErrors.investor_company_name : ""}
              />
            </Grid>
            
            {/* Country of Origin */}
            <Grid item xs={12} md={6}>
              {!isOtherCountry ? (
                <TextField
                  select
                  id="country_of_origin"
                  name="country_of_origin"
                  label="Country of Origin"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.country_of_origin || ""}
                  onChange={handleCountryChange}
                  onBlur={handleBlur}
                  error={showError("country_of_origin")}
                  helperText={showError("country_of_origin") ? formErrors.country_of_origin : "Select country of origin"}
                  SelectProps={{
                    MenuProps: {
                      style: { maxHeight: 400 }
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select a country</em>
                  </MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                  <MenuItem value="other" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    + Other (Not in list)
                  </MenuItem>
                </TextField>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    id="other_country"
                    name="other_country"
                    label="Enter Country Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={otherCountryValue}
                    onChange={handleOtherCountryChange}
                    onBlur={handleOtherCountryBlur}
                    error={showError("country_of_origin")}
                    helperText={showError("country_of_origin") ? formErrors.country_of_origin : ""}
                    autoFocus
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsOtherCountry(false);
                      setOtherCountryValue("");
                    }}
                    sx={{ minWidth: '100px' }}
                  >
                    Back to List
                  </Button>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="sector-of-interest-label">Sector of Interest</InputLabel>
                <Select
                  labelId="sector-of-interest-label"
                  id="sector_of_interest"
                  name="sector_of_interest"
                  label="Sector of Interest"
                  multiple
                  value={getSelectedSectors()}
                  onChange={handleSectorChange}
                  input={<OutlinedInput label="Sector of Interest" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {sectorOfInterestOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={getSelectedSectors().indexOf(option) > -1} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                id="representative_names"
                name="representative_names"
                label={getFieldLabel("representative_names")}
                variant="outlined"
                fullWidth
                required
                value={formData.representative_names}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("representative_names")}
                helperText={showError("representative_names") ? formErrors.representative_names : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="park-interested-label">Park Interested in</InputLabel>
                <Select
                  labelId="park-interested-label"
                  id="park_interested_in"
                  name="park_interested_in"
                  label="Park Interested in"
                  multiple
                  value={getSelectedParks()}
                  onChange={handleParkChange}
                  input={<OutlinedInput label="Park Interested in" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {loadingParks ? (
                    <MenuItem disabled>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>Loading parks...</Typography>
                    </MenuItem>
                  ) : parkInterestedOptions.length > 0 ? (
                    parkInterestedOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={getSelectedParks().indexOf(option) > -1} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No parks available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                id="purpose_of_visit"
                name="purpose_of_visit"
                label={getFieldLabel("purpose_of_visit")}
                variant="outlined"
                fullWidth
                required
                multiline
                rows={2}
                value={formData.purpose_of_visit}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("purpose_of_visit")}
                helperText={showError("purpose_of_visit") ? formErrors.purpose_of_visit : ""}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: '50%' }}>
                  <FormControl fullWidth required error={showError("how_you_know_about_us")}>
                    <InputLabel id="how-you-know-label">How you know about us</InputLabel>
                    <Select
                      labelId="how-you-know-label"
                      id="how_you_know_about_us"
                      name="how_you_know_about_us"
                      label="How you know about us"
                      value={formData.how_you_know_about_us}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <em>Select an option</em>
                      </MenuItem>
                      {howYouKnowOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {showError("how_you_know_about_us") && (
                      <Typography variant="caption" color="error">{formErrors.how_you_know_about_us}</Typography>
                    )}
                  </FormControl>
                </Box>
                
                {formData.how_you_know_about_us === "Other" && (
                  <Box sx={{ width: '50%' }}>
                    <TextField
                      id="how_you_know_about_us_other"
                      name="how_you_know_about_us_other"
                      label="Please specify"
                      variant="outlined"
                      fullWidth
                      value={formData.how_you_know_about_us_other}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={showError("how_you_know_about_us_other")}
                      helperText={showError("how_you_know_about_us_other") ? formErrors.how_you_know_about_us_other : ""}
                      placeholder="Please specify"
                      required={formData.how_you_know_about_us === "Other"}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            
            {/* Email Field with Validation */}
            <Grid item xs={12} md={6}>
              <TextField
                id="email"
                name="email"
                label={getFieldLabel("email")}
                variant="outlined"
                fullWidth
                required
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("email")}
                helperText={showError("email") ? formErrors.email : "example@domain.com"}
                inputProps={{ autoComplete: "email" }}
              />
            </Grid>
            
            {/* Phone Number Field with Validation */}
            <Grid item xs={12} md={6}>
              <TextField
                id="phone_no"
                name="phone_no"
                label={getFieldLabel("phone_no")}
                variant="outlined"
                fullWidth
                required
                type="tel"
                value={formData.phone_no}
                onChange={handleChange}
                onBlur={handleBlur}
                error={showError("phone_no")}
                helperText={showError("phone_no") ? formErrors.phone_no : "e.g., 0912345678, +251912345678"}
                inputProps={{ autoComplete: "tel" }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                id="responsible_ipdc_officer"
                name="responsible_ipdc_officer"
                label={getFieldLabel("responsible_ipdc_officer")}
                variant="outlined"
                fullWidth
                value={formData.responsible_ipdc_officer}
                disabled={true}
              />
            </Grid>
            
            <Grid item xs={12} md={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Attach Multiple Photos
              </Typography>
              <IPDCAttachFile1 
                onChange={handleAttachmentsChange} 
                isMultiple={true} 
                value={attachedFiles}
              />
            </Grid>
          </FormContainer>
        )}
        
        {activeTab === 1 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom>
              Registered Potential Investors
            </Typography>
            
            <Box sx={{ width: '100%', mb: 3 }}>
              <TextField
                variant="outlined"
                placeholder="Search investors..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: '30%' }}
                size="small"
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 110 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {paginatedInvestors.length} of {filteredInvestors.length} investors
                  {searchTerm && ` matching "${searchTerm}"`}
                </Typography>
                <Tooltip title="Export to Excel">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={exportToExcel}
                    disabled={filteredInvestors.length === 0}
                    size="small"
                  >
                    Export
                  </Button>
                </Tooltip>
              </Box>
              
              {searchTerm && (
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </Button>
              )}
            </Box>
            
            {loadingInvestors ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : filteredInvestors.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm ? 
                    `No investors found matching "${searchTerm}"` : 
                    "No investors registered yet."
                  }
                </Typography>
                {searchTerm && (
                  <Button 
                    variant="text" 
                    onClick={() => setSearchTerm("")}
                    sx={{ mt: 1 }}
                  >
                    Clear search
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{ ml: 2 }}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Date of Visit</TableCell>
                          <TableCell>Company Name</TableCell>
                          <TableCell>Country</TableCell>
                          <TableCell>Representative</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedInvestors.map((investor, index) => (
                          <TableRow key={investor.id}>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{formatDate(investor.date_of_visit)}</TableCell>
                            <TableCell>{investor.investor_company_name}</TableCell>
                            <TableCell>{investor.country_of_origin}</TableCell>
                            <TableCell>{investor.representative_names}</TableCell>
                            <TableCell>{investor.email}</TableCell>
                            <TableCell>{investor.phone_no}</TableCell>
                            <TableCell>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleView(investor)}
                                size="small"
                                title="View Details"
                              >
                                <ViewIcon />
                              </IconButton>
                              <IconButton 
                                color="secondary" 
                                onClick={() => handleEdit(investor)}
                                size="small"
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDelete(investor.id)}
                                size="small"
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={filteredInvestors.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Rows per page:"
                    />
                  </TableContainer>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Investor Details</DialogTitle>
        <DialogContent>
          {selectedInvestor && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Date of Visit</Typography>
                <Typography variant="body1">{formatDate(selectedInvestor.date_of_visit)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Company Name</Typography>
                <Typography variant="body1">{selectedInvestor.investor_company_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Country of Origin</Typography>
                <Typography variant="body1">{selectedInvestor.country_of_origin}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sector of Interest</Typography>
                <Typography variant="body1">{selectedInvestor.sector_of_interest}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Representative Names</Typography>
                <Typography variant="body1">{selectedInvestor.representative_names}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Park Interested In</Typography>
                <Typography variant="body1">{selectedInvestor.park_interested_in}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Purpose of Visit</Typography>
                <Typography variant="body1">{selectedInvestor.purpose_of_visit}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">How You Know About Us</Typography>
                <Typography variant="body1">{selectedInvestor.how_you_know_about_us}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Responsible IPDC Officer</Typography>
                <Typography variant="body1">{selectedInvestor.responsible_ipdc_officer}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedInvestor.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1">{selectedInvestor.phone_no}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </>
  );
}